import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import { ErrorDisplay } from '@/components/error';
import { LoadingIndicator } from '@/components/loading-indicator';
import { FormTitleContainer } from '@/components/styled';
import TooltipLabel from '@/components/TooltipLabel';
import Empty from '@/pages/workgroup/form/setting/schedule/empty';
import ScheduleForm from '@/pages/workgroup/form/setting/schedule/form';
import {
  useMutationAddSchedule,
  useMutationUpdateSchedule,
  useMutationDeleteSchedule,
  useQueryListSchedule,
} from '@/pages/workgroup/form/setting/schedule/hook';
import { getWeekDayLabel, Schedule, scheduleTime, ScheduleData } from '@/pages/workgroup/form/setting/schedule/type';
import { WorkspaceT, WorkSpaceType } from '@/pages/workgroup/type';
import { Button } from '@tigergraph/app-ui-lib/button';
import { Tag } from '@tigergraph/app-ui-lib/tag';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Plus } from 'baseui/icon';
import { Trash2Icon, Edit2Icon } from 'lucide-react';
import { useTour } from '@/components/onboard/TourProvider';
import { StyledToast } from '@tigergraph/app-ui-lib/styledToasterContainer';

export type ScheduleResizeHandle = {
  save: () => Promise<void>;
};

const ScheduleResize = forwardRef<
  ScheduleResizeHandle,
  {
    workspace: WorkspaceT;
    workspaceTypes: WorkSpaceType[];
    onDirtyChange: (dirty: boolean) => void;
  }
>(({ workspace, workspaceTypes, onDirtyChange }, ref) => {
  const [css] = useStyletron();
  const [showForm, setShowForm] = useState(false);
  const [editSchedule, setEditSchedule] = useState<Schedule | undefined>();
  const { data, isLoading, isError, error } = useQueryListSchedule(workspace.workgroup_id, workspace.workspace_id);

  const { setCurrentStep, steps, setIsOpen } = useTour();

  useEffect(() => {
    if (!isLoading && steps.length > 0) {
      setTimeout(() => {
        setIsOpen(true);
      }, 300);
    }
  }, [steps, setIsOpen, isLoading]);
  // Local state
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  // Store initial data for diff
  const initialSchedulesRef = useRef<Schedule[]>([]);

  // Initialize local state
  useEffect(() => {
    if (data?.Result) {
      setSchedules(data.Result);
      initialSchedulesRef.current = data.Result;
    }
  }, [data]);

  // Check if there are unsaved changes
  const isDirty = React.useMemo(() => {
    // Add/Edit/Delete all count as dirty
    const initial = initialSchedulesRef.current;
    if (schedules.length !== initial.length) return true;
    // Check if content has changed
    for (const s of schedules) {
      const old = initial.find((i) => i.id === s.id);
      if (!old) return true;
      if (JSON.stringify({ ...s, id: undefined }) !== JSON.stringify({ ...old, id: undefined })) return true;
    }
    // Check if any have been deleted
    for (const old of initial) {
      if (!schedules.find((s) => s.id === old.id)) return true;
    }
    return false;
  }, [schedules]);

  // Notify parent when isDirty changes
  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const addMutation = useMutationAddSchedule();
  const updateMutation = useMutationUpdateSchedule();
  const deleteMutation = useMutationDeleteSchedule();

  // Expose save method
  useImperativeHandle(
    ref,
    () => ({
      save: async () => {
        const group_id = workspace.workgroup_id;
        const workspace_id = workspace.workspace_id;
        const initial = initialSchedulesRef.current;
        // 1. Add
        const toAdd = schedules.filter((s) => !initial.find((i) => i.id === s.id));
        // 2. Edit
        const toUpdate = schedules.filter((s) => {
          const old = initial.find((i) => i.id === s.id);
          return old && JSON.stringify({ ...s, id: undefined }) !== JSON.stringify({ ...old, id: undefined });
        });
        // 3. Delete
        const toDelete = initial.filter((old) => !schedules.find((s) => s.id === old.id)).map((s) => s.id);
        // Execute in batch
        const addPromises = toAdd.map((s) =>
          addMutation.mutateAsync({ group_id, workspace_id, data: s as ScheduleData })
        );
        const updatePromises = toUpdate.map((s) =>
          updateMutation.mutateAsync({ group_id, workspace_id, id: s.id, data: s as ScheduleData })
        );
        const deletePromises = toDelete.map((id) => deleteMutation.mutateAsync({ group_id, workspace_id, id }));
        await Promise.all([...addPromises, ...updatePromises, ...deletePromises]);
        // Refresh initial data
        initialSchedulesRef.current = schedules;
      },
    }),
    [schedules, workspace, addMutation, updateMutation, deleteMutation]
  );

  // Add/Edit/Delete operations
  const handleAdd = (schedule: ScheduleData) => {
    setSchedules((prev) => [...prev, { ...schedule, id: `temp-${Date.now()}-${Math.random()}` }]);
    setShowForm(false);
  };
  const handleEdit = (id: string, schedule: ScheduleData) => {
    setSchedules((prev) => prev.map((s) => (s.id === id ? { ...schedule, id } : s)));
    setEditSchedule(undefined);
    setShowForm(false);
  };
  const handleDelete = (id: string) => {
    setSchedules((prev) => prev.filter((s) => s.id !== id));
  };

  return (
    <div>
      <FormTitleContainer data-tour="step-6">
        <TooltipLabel
          label="Schedule"
          tooltip={`You can create one or more schedules to resize the workspace to a specific workspace size at a specific time.`}
        />
        <Button
          kind="secondary"
          type="button"
          shape="square"
          onClick={() => {
            setShowForm(true);
            setEditSchedule(undefined);
            setCurrentStep(6);
          }}
        >
          <Plus size={20} />
        </Button>
      </FormTitleContainer>
      {isLoading ? <LoadingIndicator /> : null}
      {isError ? <ErrorDisplay label="Server error:" error={error} /> : null}
      {showForm ? (
        <ScheduleForm
          editSchedule={editSchedule}
          onClose={() => {
            setShowForm(false);
            setEditSchedule(undefined);
          }}
          workspace={workspace}
          workspaceTypes={workspaceTypes}
          onSubmit={editSchedule ? (data) => handleEdit(editSchedule.id, data) : handleAdd}
        />
      ) : null}
      <ScheduleList
        schedules={schedules}
        workspace={workspace}
        workspaceTypes={workspaceTypes}
        onEdit={(schedule) => {
          setEditSchedule(schedule);
          setShowForm(true);
        }}
        onDelete={handleDelete}
      />
      {workspace?.tg_version.startsWith('4.2.0') && (
        <div
          className={css({
            paddingTop: '16px',
          })}
        >
          <StyledToast
            hideBorder={true}
            kind="warning"
            closeable={false}
            title="Warning"
            message="Resizing TigerGraph Version 4.2.0 will result in the loss of all vector embeddings. Please note that vector embedding is a preview feature in this version. To avoid data loss, ensure you have a backup of your vector embedding data and be prepared to reload it after the resize is done."
          />
        </div>
      )}
    </div>
  );
});

export default ScheduleResize;

function ScheduleList({
  schedules,
  workspace,
  workspaceTypes,
  onEdit,
  onDelete,
}: {
  schedules: Schedule[];
  workspace: WorkspaceT;
  workspaceTypes: WorkSpaceType[];
  onEdit: (schedule: Schedule) => void;
  onDelete: (id: string) => void;
}) {
  const [css, theme] = useStyletron();
  if (!schedules || schedules.length === 0) {
    return <Empty />;
  }
  return (
    <div
      className={css({
        borderRadius: '4px',
        border: `1px solid ${theme.colors['border.tertiary']}`,
      })}
    >
      {schedules.map((schedule, index) => (
        <ScheduleItem
          schedule={schedule}
          key={schedule.id}
          showTop={index > 0}
          workspace={workspace}
          workspaceTypes={workspaceTypes}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

function ScheduleItem({
  schedule,
  showTop,
  workspace,
  workspaceTypes,
  onEdit,
  onDelete,
}: {
  schedule: Schedule;
  showTop: boolean;
  workspace: WorkspaceT;
  workspaceTypes: WorkSpaceType[];
  onEdit: (schedule: Schedule) => void;
  onDelete: (id: string) => void;
}) {
  const [css, theme] = useStyletron();
  return (
    <div
      className={css({
        borderTop: showTop ? `1px solid ${theme.colors['border.tertiary']}` : 'none',
      })}
    >
      <div
        className={css({
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          ':hover': {
            backgroundColor: theme.colors['background.tertiary'],
          },
        })}
      >
        <span
          className={css({
            color: theme.colors['text.secondary'],
            flexShrink: 0,
          })}
        >{`(${schedule.timezone})`}</span>
        <span>{`${scheduleTime(schedule)} set to`}</span>
        <span
          className={css({
            fontWeight: 600,
            flexShrink: 0,
          })}
        >{`${schedule.workspace_type_name}`}</span>
        {schedule.repeat === 'DAILY' ? <Tag closeable={false}>Everyday</Tag> : null}
        {schedule.repeat === 'WEEKDAY' ? (
          <Tag
            closeable={false}
            overrides={{
              Text: {
                style: {
                  maxWidth: 'none',
                },
              },
            }}
          >
            Every Weekday (Monday to Friday)
          </Tag>
        ) : null}
        {schedule.repeat === 'WEEKLY' ? (
          <span
            className={css({
              display: 'flex',
              flexShrink: 1,
              flexGrow: 1,
              flexBasis: 0,
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '4px',
            })}
          >
            {schedule.repeat_on.map((d) => (
              <Tag key={d} closeable={false}>
                {getWeekDayLabel(d).slice(0, 3)}
              </Tag>
            ))}
          </span>
        ) : null}
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginLeft: 'auto',
          })}
        >
          <Button shape="square" kind="text" type="button" onClick={() => onEdit(schedule)}>
            <Edit2Icon size={16} />
          </Button>
          <Button kind="text" shape="square" type="button" onClick={() => onDelete(schedule.id)}>
            <Trash2Icon size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
